import { useState, useEffect, useCallback } from 'react';
import {
  User, Mail, Building2, Shield, Loader as Loader2, CreditCard, Users,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../lib/useApi';

interface OrgMemberOption {
  user_id: string;
  full_name: string;
  email: string;
}

export default function Profile() {
  const { user, organisation, membership } = useAuth();
  const { apiGet, apiPut } = useApi();
  const [managerOptions, setManagerOptions] = useState<OrgMemberOption[]>([]);
  const [currentManagerId, setCurrentManagerId] = useState<string | null>(null);
  const [managerSaving, setManagerSaving] = useState(false);
  const [managerLoaded, setManagerLoaded] = useState(false);
  const [disabledManagerIds, setDisabledManagerIds] = useState<Set<string>>(new Set());

  const isOrgUser = !!organisation;

  const loadManagerData = useCallback(async () => {
    if (!user?.id || !organisation?.id) return;
    const { members: allMembers } = await apiGet<{ members: Array<{ user_id: string; manager_user_id: string | null; display_name?: string | null; email?: string | null }> }>(
      `/v1/org-members?org_id=${encodeURIComponent(organisation.id)}`,
    );

    const myMembership = allMembers?.find(m => m.user_id === user.id);
    if (myMembership) setCurrentManagerId(myMembership.manager_user_id);
    setManagerLoaded(true);

    if (allMembers && allMembers.length > 0) {
      const directReports = new Set<string>();
      const findReports = (managerId: string) => {
        for (const m of allMembers) {
          if (m.manager_user_id === managerId && !directReports.has(m.user_id)) {
            directReports.add(m.user_id);
            findReports(m.user_id);
          }
        }
      };
      findReports(user.id);
      setDisabledManagerIds(directReports);

      setManagerOptions(
        allMembers
          .filter(m => m.user_id !== user.id)
          .map(m => ({
            user_id: m.user_id,
            full_name: m.display_name || '',
            email: m.email || '',
          })),
      );
    }
  }, [apiGet, organisation?.id, user?.id]);

  useEffect(() => {
    if (!user?.id || !organisation?.id) return;
    loadManagerData().catch(() => {
      setManagerLoaded(true);
    });
  }, [loadManagerData, user?.id, organisation?.id]);

  const handleManagerChange = async (newManagerId: string | null) => {
    if (!user?.id || !organisation?.id) return;
    setManagerSaving(true);

    try {
      await apiPut('/v1/org-members/me/manager', {
        org_id: organisation.id,
        manager_user_id: newManagerId,
      });
      setCurrentManagerId(newManagerId);
      await loadManagerData();
    } finally {
      setManagerSaving(false);
    }
  };

  return (
    <div className="app-page">
      <div className="mb-8 border-b border-app-line pb-6">
        <p className="app-kicker mb-2">Account settings</p>
        <h1 className="font-display text-3xl font-semibold text-app-ink sm:text-4xl">Profile</h1>
        <p className="mt-2 text-sm text-app-muted">Your account, organisation and reporting information.</p>
      </div>

      <div className="max-w-2xl space-y-6">
        <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-forest-100 rounded-lg flex items-center justify-center">
              <User className="w-8 h-8 text-forest-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-app-ink">
                {user?.user_metadata?.full_name || 'User'}
              </h2>
              <p className="text-sm text-app-muted">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-app-canvas rounded-lg">
              <div className="flex items-center gap-2 text-xs text-app-muted mb-1">
                <Mail className="w-3.5 h-3.5" /> Email
              </div>
              <p className="text-sm font-medium text-app-ink">{user?.email}</p>
            </div>
            <div className="p-4 bg-app-canvas rounded-lg">
              <div className="flex items-center gap-2 text-xs text-app-muted mb-1">
                <Building2 className="w-3.5 h-3.5" /> Organisation
              </div>
              <p className="text-sm font-medium text-app-ink">{organisation?.name || '-'}</p>
            </div>
            <div className="p-4 bg-app-canvas rounded-lg">
              <div className="flex items-center gap-2 text-xs text-app-muted mb-1">
                <Shield className="w-3.5 h-3.5" /> Role
              </div>
              <p className="text-sm font-medium text-app-ink capitalize">{membership?.role || 'N/A'}</p>
            </div>
            <div className="p-4 bg-app-canvas rounded-lg">
              <div className="flex items-center gap-2 text-xs text-app-muted mb-1">
                <User className="w-3.5 h-3.5" /> User ID
              </div>
              <p className="text-sm font-mono text-app-muted truncate">{user?.id}</p>
            </div>
          </div>
        </div>

        {isOrgUser && managerLoaded && (
          <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-forest-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-app-ink">Reporting manager</h3>
                <p className="text-xs text-app-muted">Select who you report to in your organisation</p>
              </div>
            </div>
            <div className="p-4 bg-app-canvas rounded-lg">
              <label htmlFor="manager-select" className="block text-xs font-medium text-app-muted mb-2">Manager</label>
              <div className="relative">
                <select
                  id="manager-select"
                  value={currentManagerId ?? ''}
                  onChange={(e) => handleManagerChange(e.target.value || null)}
                  disabled={managerSaving}
                  className="w-full appearance-none bg-app-surface border border-app-line rounded-lg px-3 py-2.5 text-sm text-app-ink pr-8 focus:outline-none focus:ring-2 focus:ring-forest-500/30 focus:border-forest-500 disabled:opacity-50 transition-colors"
                >
                  <option value="">No manager selected</option>
                  {managerOptions.map(opt => {
                    const isDisabled = disabledManagerIds.has(opt.user_id);
                    return (
                      <option key={opt.user_id} value={opt.user_id} disabled={isDisabled}>
                        {opt.full_name || opt.email}{isDisabled ? ' (reports to you)' : ''}
                      </option>
                    );
                  })}
                </select>
                {managerSaving && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 text-app-muted animate-spin" />
                  </div>
                )}
              </div>
              <p className="text-xs text-app-muted mt-2">This determines your reporting hierarchy for territory analytics.</p>
            </div>
          </div>
        )}

        {isOrgUser && (
          <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-forest-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-forest-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-app-ink">Licence and billing</h3>
                <p className="text-xs text-app-muted">Subscription information</p>
              </div>
            </div>
            <div className="p-4 bg-app-canvas rounded-lg">
              <p className="text-sm text-app-muted">
                Your licence is managed by your organisation (<span className="font-medium text-app-ink">{organisation?.name}</span>).
                Contact your organisation administrator for billing details.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
